import React, { ReactNode } from "react";
import styled from "@emotion/styled";
import {
  ComparisonValue,
  SelectComparison,
} from "@components/Chart/BaseChartTools";
import { Theme, useMediaQuery } from "@mui/material";

const InvestmentChartBottomBarWrapper = styled("div", {
  shouldForwardProp: (prop) => prop !== "isStacked" && prop !== "hasComponent",
})<{
  isStacked: boolean;
  hasComponent: boolean;
}>`
  display: flex;
  width: 100%;
  gap: ${({ theme }) => theme.spacing(4)};
  flex-direction: ${({ isStacked }) => (isStacked ? "column" : "row")};
  justify-content: ${({ hasComponent }) =>
    hasComponent ? "space-between" : "flex-end"};
  align-items: ${({ hasComponent, isStacked }) =>
    isStacked ? "stretch" : hasComponent ? "flex-end" : "center"};
`;

const RightSide = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  flex-shrink: 0;

  ${({ theme }) => theme.breakpoints.down("sm")} {
    width: 100%;
  }
`;

interface InvestmentChartBottomBarProps {
  selectedComparedValue?: string | null;
  comparisonValues?: ComparisonValue[];
  onComparedValueChange?: (value: string | null) => void;
  disabled?: boolean;
  bottomBarComponent?: ReactNode;
  showComparisonSelector?: boolean;
}

export const InvestmentChartBottomBar = ({
  selectedComparedValue = null,
  comparisonValues = [],
  disabled,
  onComparedValueChange,
  bottomBarComponent,
  showComparisonSelector = true,
}: InvestmentChartBottomBarProps) => {
  const isMd = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const handleComparisonChange = (value: string | null) => {
    onComparedValueChange && onComparedValueChange(value);
  };

  const hasComponent = Boolean(bottomBarComponent);

  if (
    !hasComponent &&
    (comparisonValues.length === 0 || !showComparisonSelector)
  ) {
    return null;
  }

  return (
    <InvestmentChartBottomBarWrapper
      isStacked={isMd}
      hasComponent={hasComponent}
    >
      {bottomBarComponent}
      {comparisonValues.length > 0 && showComparisonSelector && (
        <RightSide>
          <SelectComparison
            disabled={disabled}
            value={selectedComparedValue}
            values={comparisonValues}
            onChange={handleComparisonChange}
            fullWidth={hasComponent ? isMd : isSm}
          />
        </RightSide>
      )}
    </InvestmentChartBottomBarWrapper>
  );
};
